import { select, call, takeEvery, put, all } from 'redux-saga/effects';
import { isAfter, parseISO, subHours, format } from 'date-fns';
import { fetchEntity } from '@classpass/saga-tools';
import { SERVER_DATE_FORMAT } from 'constants/logic';
import URLS from 'constants/urls';
import { transformClientResponse, transformKeysToSnakeCase } from 'helpers/core';
import { makePath } from 'helpers/link-generation';
import { fetchStorageForUser, overwriteStorageForUser } from 'helpers/local-storage';
import { userInfo } from 'store/saga/entities/bound';
import { selectActiveVenueId, selectRouteQuery, selectUserInfo } from 'selectors/general';
import { createErrorBanner, hideBanner } from 'components/Banner';
import { ACTIVE_VENUE_CHANGED } from 'features/App/App.saga';
import { setPike13DataFromLocalStorage, setPike13Data, setPike13IsLoading } from './Pike13.reducer';
import { selectPike13Data } from './Pike13.selectors';
import messages from './Pike13.messages';

const LOCAL_STORAGE_KEY = 'pike13migration';
const EMPTY_MIGRATION_DATA = {
  venueId: null,
  subdomain: null,
  transitionDate: null,
  locationId: null,
  classIds: [],
};

export const FORM_CHANGE = 'venuePortal/pike13Migration/FORM_CHANGE';
export const handlePike13FormChange = values => ({ type: FORM_CHANGE, values });

export function* getLocalStorage() {
  const { userId } = yield select(selectUserInfo);
  const userStorage = yield call(fetchStorageForUser, userId);
  const migrationData = userStorage[LOCAL_STORAGE_KEY] || {};
  return {
    ...migrationData,
    transitionDate: migrationData.transitionDate && parseISO(migrationData.transitionDate),
  };
}

export function* setLocalStorage(updates) {
  const { userId } = yield select(selectUserInfo);
  const userStorage = yield call(fetchStorageForUser, userId);
  const migrationData = yield call(getLocalStorage);
  const updatedMigrationData = !updates
    ? null
    : {
        ...migrationData,
        ...updates,
        transitionDate: updates.transitionDate && updates.transitionDate.toISOString(),
        lastModified: new Date().toISOString(),
      };
  const updatedUserStorage = {
    ...userStorage,
    [LOCAL_STORAGE_KEY]: updatedMigrationData,
  };
  yield call(overwriteStorageForUser, userId, updatedUserStorage);
}

export function* fetchLastReservationDate(client, activeVenueId) {
  try {
    const { httpClient } = client;
    yield put(setPike13IsLoading({ lastReservationDate: true }));
    const endpoint = URLS.pike13.info(activeVenueId);
    const resp = yield call([httpClient, httpClient.get], endpoint);
    const { lastReservationDate } = transformClientResponse(resp);
    const lastReservationDateTime = parseISO(lastReservationDate);
    yield all([
      put(setPike13IsLoading({ lastReservationDate: false })),
      put(setPike13Data({ lastReservationDate: lastReservationDateTime })),
    ]);
  } catch (error) {
    yield all([
      put(createErrorBanner(messages.ineligibleVenue)),
      put(setPike13IsLoading({ lastReservationDate: false })),
    ]);
  }
}

export function* fetchLocationsAndClasses(client, { subdomain }) {
  try {
    if (!subdomain) return;
    const { httpClient } = client;
    yield put(setPike13IsLoading({ pike13Data: true }));
    const { apiToken } = yield select(selectPike13Data);
    const locationsEndpoint = URLS.pike13.locations(subdomain, apiToken);
    const servicesEndpoint = URLS.pike13.services(subdomain, apiToken);
    const [locationsResp, servicesResp] = yield all([
      call([httpClient, httpClient.get], locationsEndpoint),
      call([httpClient, httpClient.get], servicesEndpoint),
    ]);

    yield all([
      put(hideBanner()),
      put(setPike13IsLoading({ pike13Data: false })),
      put(
        setPike13Data({
          locations: locationsResp.data.locations,
          services: servicesResp.data.services,
        })
      ),
      put(
        setPike13DataFromLocalStorage({
          locationId: locationsResp.data.locations[0].id,
        })
      ),
    ]);
  } catch (error) {
    yield all([
      put(setPike13IsLoading({ pike13Data: false })),
      put(setPike13Data({ locations: undefined, services: undefined })),
      put(setPike13DataFromLocalStorage({ locationId: undefined })),
      call(setLocalStorage, { subdomain: undefined }),
      put(createErrorBanner(messages.invalidSubdomain, { subdomain })),
    ]);
  }
}

export function* postMigrationData(client, migrationData, oauthCode) {
  try {
    const { httpClient } = client;
    const { venueId, subdomain, transitionDate, locationId, classIds } = migrationData;
    const data = {
      locationId,
      organizationName: subdomain,
      services: classIds.map(Number),
      sourceTransitionDate: format(transitionDate, SERVER_DATE_FORMAT),
      code: oauthCode,
    };
    yield put(setPike13IsLoading({ migration: true }));
    const endpoint = URLS.pike13.migrate(venueId);
    yield call([httpClient, httpClient.post], endpoint, transformKeysToSnakeCase(data));
    yield all([put(setPike13IsLoading({ migration: false })), put(setPike13Data({ isSuccess: true }))]);
    return true;
  } catch (error) {
    yield all([put(createErrorBanner(messages.integrationError)), put(setPike13IsLoading({ migration: false }))]);
    return false;
  }
}

export function* doHandleFormChange(client, action) {
  const { values } = action;
  const { localStorageData } = yield select(selectPike13Data);

  yield all([put(setPike13DataFromLocalStorage(values)), call(setLocalStorage, values)]);

  if (localStorageData.subdomain !== values.subdomain) {
    yield call(fetchLocationsAndClasses, client, values);
  }
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  yield call(client.navigate, makePath.pike13(venueId));
}

export function* handleOAuthRedirect(client) {
  const queryparams = yield select(selectRouteQuery);
  const venueId = yield select(selectActiveVenueId);
  const newPath = makePath.pike13(venueId, queryparams);
  yield call(client.navigate, newPath);
}

export function* handleRouteEnter(client) {
  const { code: oauthCode } = yield select(selectRouteQuery);
  const activeVenueId = yield select(selectActiveVenueId);
  const dataFromLocalStorage = yield call(getLocalStorage);
  const { lastModified, venueId } = dataFromLocalStorage;
  // We store because oauth requires a new page.  An hour should be plenty of time.
  const localStorageExpiry = subHours(new Date(), 1);
  const hasLocalStorageData =
    lastModified && isAfter(parseISO(lastModified), localStorageExpiry) && venueId && venueId === activeVenueId;

  let calls = [call(fetchLastReservationDate, client, activeVenueId)];

  if (hasLocalStorageData) {
    calls = calls.concat([
      put(setPike13DataFromLocalStorage(dataFromLocalStorage)),
      call(fetchLocationsAndClasses, client, dataFromLocalStorage),
    ]);
  } else {
    const formData = {
      ...EMPTY_MIGRATION_DATA,
      venueId: activeVenueId,
      transitionDate: new Date(),
    };
    calls = calls.concat([call(setLocalStorage, formData), put(setPike13DataFromLocalStorage(formData))]);
  }

  yield all(calls);

  if (hasLocalStorageData && oauthCode) {
    const success = yield call(postMigrationData, client, dataFromLocalStorage, oauthCode);

    if (success) {
      yield all([call(fetchEntity, userInfo, [client]), call(setLocalStorage, null)]);
    }
  }
}

export function* watchActions(client) {
  yield takeEvery(FORM_CHANGE, doHandleFormChange, client);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
}
